/**
 * BacklinksPanel - メモ・読書ノートの下に出す「このページへのリンク」。
 * 本の場合は、この本のハイライトを引用している本文も含める
 */

import { BookOpen, ChevronRight, CircleCheck, FileText, Highlighter } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { type Backlink, getBacklinks, type LinkTargetType } from '@/api/services/links'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { textDocumentPath } from '@/routes/paths'

const SOURCE_ICON: Record<string, typeof FileText> = {
  memo: FileText,
  product_note: BookOpen,
  todo: CircleCheck,
}

type Props = {
  targetType: LinkTargetType
  targetId: number
}

export const BacklinksPanel = ({ targetType, targetId }: Props) => {
  const [open, setOpen] = useState(true)
  const [items, setItems] = useState<Backlink[]>([])

  useEffect(() => {
    let cancelled = false
    setItems([])
    getBacklinks(targetType, targetId)
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch((err) => console.warn('[backlinks] failed to load', err))
    return () => {
      cancelled = true
    }
  }, [targetType, targetId])

  if (items.length === 0) return null

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-6 mt-6 border-t pt-3 sm:mx-0">
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
        このページへのリンク
        <span className="text-xs font-normal">{items.length} 件</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-2 space-y-1">
          {items.map((item) => {
            const path = textDocumentPath(item.source_type, item.source_id)
            const Icon = SOURCE_ICON[item.source_type] ?? FileText
            if (!path) return null
            return (
              <li key={`${item.source_type}:${item.source_id}`}>
                <Link to={path} className="flex gap-2 rounded-md px-2 py-1.5 hover:bg-accent/60">
                  <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-1.5 text-sm">
                      <span className="truncate">{item.title}</span>
                      {item.via === 'highlight' && (
                        <span className="flex shrink-0 items-center gap-0.5 rounded bg-amber-500/15 px-1 text-[10px] text-amber-700 dark:text-amber-400">
                          <Highlighter className="size-3" />
                          ハイライトを引用
                        </span>
                      )}
                    </span>
                    {item.preview && <span className="line-clamp-2 text-xs text-muted-foreground">{item.preview}</span>}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
